import { useState } from "react";

import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import {
  Loader2, Plus, Trash2, Tags, ChevronDown, ChevronRight,
  Edit, Lock, ArrowUp, ArrowDown} from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Badge } from "@/components/ui/badge";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle,
} from "@/components/ui/dialog";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";
import { PageHeader } from "@/components/PageHeader";
import { useToast } from "@/hooks/use-toast";
import { useAuth } from "@/lib/auth-context";
import { api } from "@/lib/api";

interface ClassificationCode {
  id: string;
  schemeId: string;
  code: string;
  label: string;
  description: string | null;
  color: string | null;
  parentCodeId: string | null;
  sortOrder: number;
  isActive: boolean;
}

interface ClassificationScheme {
  id: string;
  name: string;
  slug: string;
  description: string | null;
  entityTypes: string[];
  isActive: boolean;
  isSystem: boolean;
  codes: ClassificationCode[];
}

const ENTITY_TYPE_OPTIONS = [
  { value: "account", label: "Accounts" },
  { value: "lead", label: "Leads" },
  { value: "product", label: "Products" },
  { value: "vendor", label: "Vendors" },
  { value: "opportunity", label: "Opportunities" },
];

const COLOR_OPTIONS = ["none", "red", "orange", "yellow", "green", "blue", "indigo", "purple", "pink"];

function slugify(s: string) {
  return s.toLowerCase().trim().replace(/[^a-z0-9\s-]/g, "").replace(/\s+/g, "-");
}

function codify(s: string) {
  return s.toLowerCase().trim().replace(/[^a-z0-9\s]+/g, "_").replace(/\s+/g, "_");
}

function buildCodeTree(codes: ClassificationCode[]): (ClassificationCode & { depth: number })[] {
  const childMap = new Map<string | null, ClassificationCode[]>();
  for (const c of codes) {
    const key = c.parentCodeId || null;
    if (!childMap.has(key)) childMap.set(key, []);
    childMap.get(key)!.push(c);
  }
  const result: (ClassificationCode & { depth: number })[] = [];
  function walk(parentId: string | null, depth: number) {
    const children = childMap.get(parentId) || [];
    for (const c of children) {
      result.push({ ...c, depth });
      walk(c.id, depth + 1);
    }
  }
  walk(null, 0);
  return result;
}

export default function ClassificationsSettingsPage() {
  const { sessionId } = useAuth();

  const { toast } = useToast();
  const queryClient = useQueryClient();

  const [expandedScheme, setExpandedScheme] = useState<string | null>(null);
  const [schemeDialog, setSchemeDialog] = useState(false);
  const [codeDialog, setCodeDialog] = useState(false);
  const [editingScheme, setEditingScheme] = useState<ClassificationScheme | null>(null);
  const [editingCode, setEditingCode] = useState<ClassificationCode | null>(null);
  const [activeSchemeId, setActiveSchemeId] = useState<string | null>(null);

  const [schemeForm, setSchemeForm] = useState({
    name: "", slug: "", description: "",
    entityTypes: ["account"] as string[], isActive: true,
  });
  const [codeForm, setCodeForm] = useState({
    code: "", label: "", description: "",
    color: "none", parentCodeId: "__none__", sortOrder: 0, isActive: true,
  });

  const { data: schemes = [] } = useQuery<ClassificationScheme[]>({
    queryKey: ["/api/classification-schemes"],
    queryFn: () => api.get<ClassificationScheme[]>(`/classification-schemes`, sessionId),
  });

  const saveSchemeMutation = useMutation({
    mutationFn: () => editingScheme
      ? api.patch(`/classification-schemes/${editingScheme.id}`, schemeForm, sessionId)
      : api.post(`/classification-schemes`, schemeForm, sessionId),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["/api/classification-schemes"] });
      setSchemeDialog(false);
      setEditingScheme(null);
      toast({ title: editingScheme ? "Scheme updated" : "Scheme created" });
    },
    onError: (e: Error) => toast({ title: "Error", description: e.message, variant: "destructive" }),
  });

  const deleteSchemeMutation = useMutation({
    mutationFn: (id: string) => api.delete(`/classification-schemes/${id}`, sessionId),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["/api/classification-schemes"] });
      toast({ title: "Scheme deleted" });
    },
    onError: (e: Error) => toast({ title: "Error", description: e.message, variant: "destructive" }),
  });

  const saveCodeMutation = useMutation({
    mutationFn: () => {
      const payload = {
        ...codeForm,
        parentCodeId: codeForm.parentCodeId === "__none__" ? null : codeForm.parentCodeId,
      };
      return editingCode
        ? api.patch(`/classification-codes/${editingCode.id}`, payload, sessionId)
        : api.post(`/classification-schemes/${activeSchemeId}/codes`, payload, sessionId);
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["/api/classification-schemes"] });
      setCodeDialog(false);
      setEditingCode(null);
      toast({ title: editingCode ? "Code updated" : "Code created" });
    },
    onError: (e: Error) => toast({ title: "Error", description: e.message, variant: "destructive" }),
  });

  const deleteCodeMutation = useMutation({
    mutationFn: (id: string) => api.delete(`/classification-codes/${id}`, sessionId),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["/api/classification-schemes"] });
      toast({ title: "Code deleted" });
    },
  });

  const reorderMutation = useMutation({
    mutationFn: ({ schemeId, orderedIds }: { schemeId: string; orderedIds: string[] }) =>
      api.post(`/classification-schemes/${schemeId}/reorder`, { orderedIds }, sessionId),
    onSuccess: () => queryClient.invalidateQueries({ queryKey: ["/api/classification-schemes"] }),
  });

  const handleNewScheme = () => {
    setEditingScheme(null);
    setSchemeForm({ name: "", slug: "", description: "", entityTypes: ["account"], isActive: true });
    setSchemeDialog(true);
  };

  const handleEditScheme = (s: ClassificationScheme) => {
    setEditingScheme(s);
    setSchemeForm({
      name: s.name, slug: s.slug, description: s.description || "",
      entityTypes: s.entityTypes || [], isActive: s.isActive,
    });
    setSchemeDialog(true);
  };

  const handleNewCode = (schemeId: string) => {
    const s = schemes.find(sc => sc.id === schemeId);
    setActiveSchemeId(schemeId);
    setEditingCode(null);
    setCodeForm({
      code: "", label: "", description: "",
      color: "none", parentCodeId: "__none__",
      sortOrder: s?.codes.length || 0, isActive: true,
    });
    setCodeDialog(true);
  };

  const handleEditCode = (code: ClassificationCode) => {
    setActiveSchemeId(code.schemeId);
    setEditingCode(code);
    setCodeForm({
      code: code.code, label: code.label,
      description: code.description || "",
      color: code.color || "none",
      parentCodeId: code.parentCodeId || "__none__",
      sortOrder: code.sortOrder,
      isActive: code.isActive,
    });
    setCodeDialog(true);
  };

  const handleMoveCode = (s: ClassificationScheme, codeId: string, dir: "up" | "down") => {
    const tree = buildCodeTree(s.codes);
    const idx = tree.findIndex(c => c.id === codeId);
    const swap = dir === "up" ? idx - 1 : idx + 1;
    if (idx < 0 || swap < 0 || swap >= tree.length) return;
    const next = [...tree];
    [next[idx], next[swap]] = [next[swap], next[idx]];
    reorderMutation.mutate({ schemeId: s.id, orderedIds: next.map(c => c.id) });
  };

  const activeScheme = schemes.find(s => s.id === activeSchemeId);

  return (
    <div className="space-y-6">
      <PageHeader
        title="Classifications"
        description="Manage classification schemes and codes for organizing entities"
        actions={
          <div className="flex items-center gap-2">
            <Button onClick={handleNewScheme} data-testid="button-new-scheme">
              <Plus className="h-4 w-4 mr-1" /> New Scheme
            </Button>
          </div>
        }
      />

      <div className="space-y-3">
        {schemes.length === 0 ? (
          <div className="flex flex-col items-center justify-center py-12 text-center">
            <Tags className="h-12 w-12 text-muted-foreground/50 mb-4" />
            <h3 className="text-lg font-medium">No classification schemes</h3>
            <p className="text-sm text-muted-foreground mt-1">
              Create your first scheme to start organizing entities.
            </p>
          </div>
        ) : (
          schemes.map((scheme) => {
            const isExpanded = expandedScheme === scheme.id;
            return (
              <div
                key={scheme.id}
                className="border-l-[3px] border-indigo-500 bg-card rounded-r-lg shadow-sm"
              >
                <div
                  className="flex items-center justify-between px-4 py-3 cursor-pointer hover:bg-accent/30 transition-colors"
                  onClick={() => setExpandedScheme(isExpanded ? null : scheme.id)}
                >
                  <div className="flex items-center gap-3">
                    {isExpanded
                      ? <ChevronDown className="h-4 w-4 text-muted-foreground" />
                      : <ChevronRight className="h-4 w-4 text-muted-foreground" />}
                    <div>
                      <div className="flex items-center gap-2">
                        <h3 className="text-sm font-semibold">{scheme.name}</h3>
                        {scheme.isSystem && (
                          <Badge variant="secondary" className="text-xs gap-1">
                            <Lock className="h-3 w-3" /> System
                          </Badge>
                        )}
                        {!scheme.isActive && <Badge variant="outline" className="text-xs">Inactive</Badge>}
                      </div>
                      <div className="flex items-center gap-2 mt-0.5">
                        <span className="text-xs text-muted-foreground">{scheme.codes.length} codes</span>
                        <span className="text-xs text-muted-foreground">·</span>
                        <span className="text-xs text-muted-foreground">
                          {(scheme.entityTypes || []).map(t => t.charAt(0).toUpperCase() + t.slice(1)).join(", ") || "—"}
                        </span>
                      </div>
                    </div>
                  </div>
                  <div className="flex items-center gap-1" onClick={(e) => e.stopPropagation()}>
                    <Button variant="ghost" size="sm" onClick={() => handleEditScheme(scheme)}>
                      <Edit className="h-3.5 w-3.5" />
                    </Button>
                    {!scheme.isSystem && (
                      <Button
                        variant="ghost" size="sm"
                        onClick={() => {
                          if (confirm(`Delete scheme "${scheme.name}"? All codes will be deleted.`)) {
                            deleteSchemeMutation.mutate(scheme.id);
                          }
                        }}
                      >
                        <Trash2 className="h-3.5 w-3.5 text-destructive" />
                      </Button>
                    )}
                  </div>
                </div>

                {isExpanded && (
                  <div className="px-4 pb-4 border-t">
                    {scheme.description && (
                      <p className="text-xs text-muted-foreground mt-2 mb-3">{scheme.description}</p>
                    )}
                    <div className="space-y-1.5 mt-2">
                      {buildCodeTree(scheme.codes).map((code) => (
                        <div
                          key={code.id}
                          className="flex items-center justify-between px-3 py-2 rounded-md bg-muted/50 hover:bg-muted transition-colors"
                          style={{ paddingLeft: `${12 + code.depth * 20}px` }}
                        >
                          <div className="flex items-center gap-2 flex-wrap">
                            {code.depth > 0 && <span className="text-muted-foreground text-xs">└</span>}
                            {code.color && (
                              <span className={`h-2.5 w-2.5 rounded-full bg-${code.color}-500`} />
                            )}
                            <span className="text-sm font-medium">{code.label}</span>
                            <span className="text-xs text-muted-foreground font-mono">{code.code}</span>
                            {!code.isActive && <Badge variant="outline" className="text-[10px]">Inactive</Badge>}
                          </div>
                          <div className="flex items-center gap-0.5">
                            <Button
                              variant="ghost" size="icon" className="h-6 w-6"
                              onClick={() => handleMoveCode(scheme, code.id, "up")}
                            >
                              <ArrowUp className="h-3 w-3" />
                            </Button>
                            <Button
                              variant="ghost" size="icon" className="h-6 w-6"
                              onClick={() => handleMoveCode(scheme, code.id, "down")}
                            >
                              <ArrowDown className="h-3 w-3" />
                            </Button>
                            <Button
                              variant="ghost" size="icon" className="h-6 w-6"
                              onClick={() => handleEditCode(code)}
                            >
                              <Edit className="h-3 w-3" />
                            </Button>
                            <Button
                              variant="ghost" size="icon" className="h-6 w-6"
                              onClick={() => {
                                if (confirm(`Delete code "${code.label}"?`)) deleteCodeMutation.mutate(code.id);
                              }}
                            >
                              <Trash2 className="h-3 w-3 text-destructive" />
                            </Button>
                          </div>
                        </div>
                      ))}
                    </div>
                    <Button
                      variant="outline" size="sm" className="mt-3"
                      onClick={() => handleNewCode(scheme.id)}
                    >
                      <Plus className="h-3.5 w-3.5 mr-1.5" /> Add Code
                    </Button>
                  </div>
                )}
              </div>
            );
          })
        )}
      </div>

      {/* Scheme dialog */}
      <Dialog open={schemeDialog} onOpenChange={setSchemeDialog}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>{editingScheme ? "Edit Scheme" : "New Classification Scheme"}</DialogTitle>
          </DialogHeader>
          <div className="space-y-4">
            <div>
              <label className="text-sm font-medium mb-1 block">Name *</label>
              <Input
                value={schemeForm.name}
                onChange={(e) => {
                  const name = e.target.value;
                  setSchemeForm({
                    ...schemeForm,
                    name,
                    slug: editingScheme ? schemeForm.slug : slugify(name),
                  });
                }}
              />
            </div>
            <div>
              <label className="text-sm font-medium mb-1 block">Slug *</label>
              <Input
                value={schemeForm.slug}
                onChange={(e) => setSchemeForm({ ...schemeForm, slug: e.target.value })}
              />
            </div>
            <div>
              <label className="text-sm font-medium mb-1 block">Description</label>
              <Textarea
                value={schemeForm.description}
                onChange={(e) => setSchemeForm({ ...schemeForm, description: e.target.value })}
                rows={2}
              />
            </div>
            <div>
              <label className="text-sm font-medium mb-1 block">Applies to</label>
              <div className="flex flex-wrap gap-2">
                {ENTITY_TYPE_OPTIONS.map((opt) => {
                  const isSelected = schemeForm.entityTypes.includes(opt.value);
                  return (
                    <Badge
                      key={opt.value}
                      variant={isSelected ? "default" : "outline"}
                      className="cursor-pointer"
                      onClick={() => {
                        const next = isSelected
                          ? schemeForm.entityTypes.filter(v => v !== opt.value)
                          : [...schemeForm.entityTypes, opt.value];
                        setSchemeForm({ ...schemeForm, entityTypes: next });
                      }}
                    >
                      {opt.label}
                    </Badge>
                  );
                })}
              </div>
            </div>
            <label className="flex items-center gap-2 text-sm">
              <input
                type="checkbox"
                checked={schemeForm.isActive}
                onChange={(e) => setSchemeForm({ ...schemeForm, isActive: e.target.checked })}
              />
              Active
            </label>
            <div className="flex justify-end gap-2 pt-2">
              <Button variant="outline" onClick={() => setSchemeDialog(false)}>Cancel</Button>
              <Button
                onClick={() => saveSchemeMutation.mutate()}
                disabled={!schemeForm.name || !schemeForm.slug || schemeForm.entityTypes.length === 0 || saveSchemeMutation.isPending}
              >
                {saveSchemeMutation.isPending && <Loader2 className="h-4 w-4 mr-2 animate-spin" />}
                {editingScheme ? "Update" : "Create"}
              </Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>

      {/* Code dialog */}
      <Dialog open={codeDialog} onOpenChange={setCodeDialog}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>{editingCode ? "Edit Code" : "New Classification Code"}</DialogTitle>
          </DialogHeader>
          <div className="space-y-4">
            <div>
              <label className="text-sm font-medium mb-1 block">Label *</label>
              <Input
                value={codeForm.label}
                onChange={(e) => {
                  const label = e.target.value;
                  setCodeForm({
                    ...codeForm,
                    label,
                    code: editingCode ? codeForm.code : codify(label),
                  });
                }}
              />
            </div>
            <div>
              <label className="text-sm font-medium mb-1 block">Code *</label>
              <Input
                className="font-mono"
                value={codeForm.code}
                onChange={(e) => setCodeForm({ ...codeForm, code: e.target.value })}
              />
            </div>
            <div>
              <label className="text-sm font-medium mb-1 block">Description</label>
              <Textarea
                value={codeForm.description}
                onChange={(e) => setCodeForm({ ...codeForm, description: e.target.value })}
                rows={2}
              />
            </div>
            <div>
              <label className="text-sm font-medium mb-1 block">Parent Code</label>
              <Select
                value={codeForm.parentCodeId}
                onValueChange={(v) => setCodeForm({ ...codeForm, parentCodeId: v })}
              >
                <SelectTrigger><SelectValue placeholder="None (top-level)" /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="__none__">None (top-level)</SelectItem>
                  {(activeScheme?.codes || [])
                    .filter(c => c.id !== editingCode?.id)
                    .map(c => (
                      <SelectItem key={c.id} value={c.id}>{c.label}</SelectItem>
                    ))}
                </SelectContent>
              </Select>
            </div>
            <div className="grid grid-cols-2 gap-3">
              <div>
                <label className="text-sm font-medium mb-1 block">Color</label>
                <select
                  className="w-full h-9 rounded-md border border-input bg-background px-3 text-sm"
                  value={codeForm.color}
                  onChange={(e) => setCodeForm({ ...codeForm, color: e.target.value })}
                >
                  {COLOR_OPTIONS.map(c => <option key={c} value={c}>{c}</option>)}
                </select>
              </div>
              <div>
                <label className="text-sm font-medium mb-1 block">Sort Order</label>
                <Input
                  type="number" min={0}
                  value={codeForm.sortOrder}
                  onChange={(e) => setCodeForm({ ...codeForm, sortOrder: Number(e.target.value) || 0 })}
                />
              </div>
            </div>
            <label className="flex items-center gap-2 text-sm">
              <input
                type="checkbox"
                checked={codeForm.isActive}
                onChange={(e) => setCodeForm({ ...codeForm, isActive: e.target.checked })}
              />
              Active
            </label>
            <div className="flex justify-end gap-2 pt-2">
              <Button variant="outline" onClick={() => setCodeDialog(false)}>Cancel</Button>
              <Button
                onClick={() => saveCodeMutation.mutate()}
                disabled={!codeForm.label || !codeForm.code || saveCodeMutation.isPending}
              >
                {saveCodeMutation.isPending && <Loader2 className="h-4 w-4 mr-2 animate-spin" />}
                {editingCode ? "Update" : "Create"}
              </Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
}